// frontend/src/pages/CustodiePrelevatePage.js
/**
 * "Custodie prelevate": quando la custodia di un DVD usato viene riusata per un film ex noleggio,
 * il DVD senza custodia va spostato da "DVD USATI LIB" a "DVD USATI LIB - BUSTA".
 * Si scansiona il barcode durante la giornata (la lista resta salvata nel browser) e a fine
 * preparazione ordini si spostano tutti con un solo pulsante.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Container, Card, Form, Button, Table, Alert, Badge, Row, Col, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { cercaInUbicazione, spostaUbicazioneMassivo } from '../services/api';

const CHIAVE_LISTA = 'custodie_prelevate_v1';
const DA_DEFAULT = 'DVD USATI LIB';
const A_DEFAULT = 'DVD USATI LIB - BUSTA';

const leggiLista = () => {
    try {
        const grezza = localStorage.getItem(CHIAVE_LISTA);
        const lista = grezza ? JSON.parse(grezza) : [];
        return Array.isArray(lista) ? lista : [];
    } catch (e) {
        return [];
    }
};

const CustodiePrelevatePage = () => {
    const [da, setDa] = useState(DA_DEFAULT);
    const [a, setA] = useState(A_DEFAULT);
    const [codice, setCodice] = useState('');
    const [lista, setLista] = useState(leggiLista);
    const [cercando, setCercando] = useState(false);
    const [scelta, setScelta] = useState(null);      // più film con lo stesso codice: quale?
    const [avviso, setAvviso] = useState(null);      // { variant, testo }
    const [spostando, setSpostando] = useState(false);
    const [esito, setEsito] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
        try {
            localStorage.setItem(CHIAVE_LISTA, JSON.stringify(lista));
        } catch (e) {
            // storage non disponibile: la lista vale solo per questa sessione
        }
    }, [lista]);

    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    const aggiungi = (film) => {
        const disponibili = film.quantita_in_ubicazione;
        const esistente = lista.find(r => r.id === film.id);
        if (esistente) {
            if (esistente.quantita >= disponibili) {
                setAvviso({ variant: 'warning', testo: `"${film.titolo}" è già in lista con tutte le copie disponibili (${disponibili}).` });
                return;
            }
            setLista(prev => prev.map(r => (r.id === film.id ? { ...r, quantita: r.quantita + 1 } : r)));
        } else {
            setLista(prev => [...prev, {
                id: film.id,
                sku: film.sku,
                titolo: film.titolo,
                supporto: film.supporto,
                condizione: film.condizione,
                disponibili,
                quantita: 1,
            }]);
        }
        setAvviso({ variant: 'success', testo: `Aggiunto: ${film.titolo}` });
    };

    const cerca = async (e) => {
        e.preventDefault();
        const valore = codice.trim();
        if (!valore) return;
        setCercando(true);
        setScelta(null);
        setAvviso(null);
        setEsito(null);
        try {
            const res = await cercaInUbicazione(valore, da.trim());
            const { candidati, altrove } = res.data;
            if (candidati.length === 1) {
                aggiungi(candidati[0]);
            } else if (candidati.length > 1) {
                setScelta(candidati);
            } else if (altrove.length > 0) {
                // Elenca tutti i film con questo codice e dove sono (uno potrebbe essere gia' in busta)
                const righe = altrove.map(f => {
                    const dove = f.ubicazioni.map(u => u.ubicazione).join(', ') || 'nessuna ubicazione';
                    return `${f.titolo} (${f.sku}${f.condizione ? `, ${f.condizione}` : ''}): ${dove}`;
                });
                const giaInBusta = altrove.some(f => f.ubicazioni.some(u => u.ubicazione === a.trim()));
                setAvviso({
                    variant: 'warning',
                    testo: `Nessuna copia in "${da}"${giaInBusta ? ` — risulta già in "${a}", niente da spostare` : ''}. Film con questo codice: ${righe.join(' · ')}`,
                });
            } else {
                setAvviso({ variant: 'danger', testo: `Nessun film con il codice ${valore}.` });
            }
        } catch (err) {
            setAvviso({ variant: 'danger', testo: err.response?.data?.detail || 'Errore durante la ricerca.' });
        } finally {
            setCercando(false);
            setCodice('');
            if (inputRef.current) inputRef.current.focus();
        }
    };

    const cambiaQuantita = (id, valore) => {
        setLista(prev => prev.map(r => {
            if (r.id !== id) return r;
            const n = Math.max(1, Math.min(r.disponibili, parseInt(valore, 10) || 1));
            return { ...r, quantita: n };
        }));
    };

    const rimuovi = (id) => setLista(prev => prev.filter(r => r.id !== id));

    const sposta = async () => {
        if (lista.length === 0 || !a.trim()) return;
        const totale = lista.reduce((s, r) => s + r.quantita, 0);
        if (!window.confirm(`Spostare ${lista.length} film (${totale} copie) da "${da}" a "${a}"?`)) return;

        setSpostando(true);
        setAvviso(null);
        try {
            // l'endpoint ha una quantità unica per chiamata: si raggruppa per quantità (di solito è sempre 1)
            const perQuantita = {};
            lista.forEach(r => { (perQuantita[r.quantita] = perQuantita[r.quantita] || []).push(r.id); });

            const spostati = [];
            const saltati = [];
            for (const [quantita, ids] of Object.entries(perQuantita)) {
                const res = await spostaUbicazioneMassivo({
                    film_ids: ids,
                    ubicazione_da: da.trim(),
                    ubicazione_a: a.trim(),
                    quantita: parseInt(quantita, 10),
                    motivo: 'custodia riusata per un film ex noleggio',
                });
                spostati.push(...res.data.spostati);
                saltati.push(...res.data.saltati);
            }

            const idSpostati = new Set(spostati.map(s => s.id));
            setLista(prev => prev.filter(r => !idSpostati.has(r.id)));
            setEsito({ spostati, saltati });
            if (saltati.length === 0) toast.success(`${spostati.length} film spostati in "${a}".`);
            else toast.warning(`${spostati.length} spostati, ${saltati.length} non spostati: vedi l'elenco.`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Errore durante lo spostamento.');
        } finally {
            setSpostando(false);
        }
    };

    const svuota = () => {
        if (lista.length === 0) return;
        if (window.confirm('Svuotare la lista senza spostare nulla?')) setLista([]);
    };

    return (
        <Container fluid className="py-3" style={{ maxWidth: 900 }}>
            <h2 className="h3 mb-1">Custodie prelevate</h2>
            <p className="text-muted small">
                Scansiona il barcode del DVD da cui hai tolto la custodia. A fine preparazione ordini premi "Sposta"
                e tutti i DVD in lista passano da <strong>{da}</strong> a <strong>{a}</strong>.
                La lista resta salvata in questo browser.
            </p>

            <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                    <Form onSubmit={cerca}>
                        <Row className="g-2 align-items-end">
                            <Col xs={12} md={8}>
                                <Form.Label className="small mb-1">Barcode, barcode IBS o SKU</Form.Label>
                                <Form.Control
                                    ref={inputRef}
                                    value={codice}
                                    onChange={(e) => setCodice(e.target.value)}
                                    placeholder="Scansiona o scrivi e premi Invio"
                                    autoComplete="off"
                                    inputMode="numeric"
                                />
                            </Col>
                            <Col xs={12} md={4}>
                                <Button type="submit" variant="primary" className="w-100" disabled={cercando || !codice.trim()}>
                                    {cercando ? <Spinner as="span" animation="border" size="sm" /> : 'Aggiungi alla lista'}
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    {avviso && <Alert variant={avviso.variant} className="mt-3 mb-0 py-2">{avviso.testo}</Alert>}

                    {scelta && (
                        <div className="mt-3">
                            <div className="small text-muted mb-1">Più film con questo codice: quale hai usato?</div>
                            {scelta.map(f => (
                                <Button
                                    key={f.id}
                                    variant="outline-primary"
                                    className="me-2 mb-2 text-start"
                                    onClick={() => { aggiungi(f); setScelta(null); if (inputRef.current) inputRef.current.focus(); }}
                                >
                                    {f.titolo}
                                    <div className="small">{f.sku} · {f.condizione || 'condizione n.d.'} · {f.quantita_in_ubicazione} in {da}</div>
                                </Button>
                            ))}
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-3">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Da spostare <Badge bg="secondary">{lista.length}</Badge></h5>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={svuota} disabled={lista.length === 0 || spostando}>Svuota</Button>
                        <Button variant="success" size="sm" onClick={sposta} disabled={lista.length === 0 || spostando || !a.trim()}>
                            {spostando ? <Spinner as="span" animation="border" size="sm" /> : `Sposta in BUSTA (${lista.length})`}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    {lista.length === 0 ? (
                        <div className="text-center text-muted py-4">Nessun DVD in lista</div>
                    ) : (
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="table-light">
                                <tr><th>Titolo</th><th>SKU</th><th style={{ width: 110 }}>Copie</th><th></th></tr>
                            </thead>
                            <tbody>
                                {lista.map(r => (
                                    <tr key={r.id}>
                                        <td>{r.titolo}<div className="small text-muted">{r.supporto || ''}{r.condizione ? ` · ${r.condizione}` : ''}</div></td>
                                        <td className="small text-muted">{r.sku}</td>
                                        <td>
                                            <Form.Control
                                                type="number" min="1" max={r.disponibili} size="sm"
                                                value={r.quantita}
                                                onChange={(e) => cambiaQuantita(r.id, e.target.value)}
                                            />
                                            <div className="small text-muted">su {r.disponibili}</div>
                                        </td>
                                        <td className="text-end">
                                            <Button variant="outline-danger" size="sm" onClick={() => rimuovi(r.id)} aria-label="Rimuovi">&times;</Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {esito && (
                <Alert variant={esito.saltati.length ? 'warning' : 'success'}>
                    <div className="fw-semibold">{esito.spostati.length} film spostati in "{a}".</div>
                    {esito.saltati.length > 0 && (
                        <>
                            <div className="small mt-1">Non spostati (restano in lista):</div>
                            <ul className="small mb-0">
                                {esito.saltati.map(s => <li key={s.id}>{s.titolo || `Film ${s.id}`}: {s.motivo}</li>)}
                            </ul>
                        </>
                    )}
                </Alert>
            )}

            <details className="small text-muted">
                <summary>Cambia ubicazioni</summary>
                <Row className="g-2 mt-1">
                    <Col xs={12} md={6}>
                        <Form.Label className="mb-1">Da</Form.Label>
                        <Form.Control size="sm" value={da} onChange={(e) => setDa(e.target.value)} />
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Label className="mb-1">A</Form.Label>
                        <Form.Control size="sm" value={a} onChange={(e) => setA(e.target.value)} />
                    </Col>
                </Row>
            </details>
        </Container>
    );
};

export default CustodiePrelevatePage;
